import wx from "weixin-js-sdk";
import { getObjectValue } from "../string/getObjectValue";
import { getApiDatas, saveUserInfo } from "../apis";
import { VueCookieNext } from 'vue-cookie-next';
import { getUserLastRequestUrl, isLocalWeb, isLogin } from "./login";

/**
 * 是否强制设置当前环境为微信小程序
 */
export function isForceInWechatMiniProgram() {
    if (!isLocalWeb()) {
        return false;
    }
    return false;
}

/** 首次 getEnv 结果缓存，避免每次路由都等回调 */
let cachedIsMiniProgram = null;

function getUa() {
    try {
        return String((typeof navigator !== 'undefined' && navigator.userAgent) || '').toLowerCase();
    } catch (e) {
        return '';
    }
}

/** 明确的小程序 web-view（不依赖 getEnv） */
function isDefinitelyMiniProgram() {
    try {
        if (typeof window !== 'undefined' && window.__wxjs_environment === 'miniprogram') {
            return true;
        }
    } catch (e) {
        /* ignore */
    }
    const ua = getUa();
    if (ua.indexOf('miniprogram') !== -1) return true;
    // uni-app 嵌套 web-view
    if (ua.indexOf('micromessenger') !== -1 && ua.indexOf('uni-app') !== -1) return true;
    return false;
}

/** 同步判断：避免接口 -444 时在 getEnv 回调前误跳 H5 /login */
export function isWechatMiniProgramSync() {
    if (isForceInWechatMiniProgram() || isDefinitelyMiniProgram()) {
        return true;
    }
    return cachedIsMiniProgram === true;
}

/**
 * 判断是否在微信小程序 web-view 中。
 * 能立刻判定则立刻回调；getEnv 超时不再写成「非小程序」，避免先 H5 登录再授权。
 */
export function isInWechatApp(succ = null, fail = null) {
    if (typeof succ != 'function') succ = () => {};
    if (typeof fail != 'function') fail = () => {};

    let settled = false;
    const done = (ok) => {
        if (settled) return;
        settled = true;
        if (ok) succ();
        else fail();
    };

    if (isForceInWechatMiniProgram() || isDefinitelyMiniProgram()) {
        cachedIsMiniProgram = true;
        done(true);
        return true;
    }

    if (cachedIsMiniProgram === true) {
        done(true);
        return true;
    }
    if (cachedIsMiniProgram === false) {
        done(false);
        return false;
    }

    try {
        const ua = getUa();
        if (ua.match(/MicroMessenger/i) == "micromessenger") {
            const timer = setTimeout(() => {
                // 超时仍按小程序处理（本项目为小程序 web-view），避免先走 H5 /login
                cachedIsMiniProgram = true;
                done(true);
            }, 400);

            try {
                if (!wx || !wx.miniProgram || typeof wx.miniProgram.getEnv !== 'function') {
                    clearTimeout(timer);
                    cachedIsMiniProgram = true;
                    done(true);
                    return true;
                }
                wx.miniProgram.getEnv((res) => {
                    clearTimeout(timer);
                    const ok = !!(res && res.miniprogram);
                    cachedIsMiniProgram = ok;
                    done(ok);
                });
            } catch (e) {
                clearTimeout(timer);
                console.log('err: ', e);
                cachedIsMiniProgram = true;
                done(true);
            }
            return true;
        }
        cachedIsMiniProgram = false;
        done(false);
    } catch (e) {
        console.log('err: ', e);
        done(false);
    }
    return false;
}

/**
 * 跳转到微信小程序登录页面。
 * - 调用当下同步加锁，避免真机多接口错开返回叠多个授权页
 * - 仅在「从授权页回到 web-view」后解锁，便于取消后再拉
 * - 真机 web-view 常一直 visibility=visible，禁止用 visible 直接解锁
 */
let lastGotoWechatLoginAt = 0;
/** 合并短时间并发；解锁后可再次拉起 */
const GOTO_WECHAT_LOGIN_DEBOUNCE_MS = 2000;
let wechatWxAuthInFlight = false;
let wechatGrantOpened = false;
/** navigateTo 发出到 success/fail 之间的同步锁 */
let wechatGrantNavigating = false;
let grantCoveredByNative = false;
let grantVisibilityBound = false;

try {
    if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('Wechat-Grant-Opened-at');
    }
} catch (e) {
    /* ignore */
}

/** 允许再次拉起授权（取消/失败回前台后调用） */
export function resetWechatGrantLock() {
    wechatGrantOpened = false;
    wechatGrantNavigating = false;
    grantCoveredByNative = false;
    lastGotoWechatLoginAt = 0;
}

function bindGrantVisibilityUnlock() {
    if (grantVisibilityBound || typeof document === 'undefined') return;
    grantVisibilityBound = true;

    const onPossiblyReturnedFromGrant = () => {
        if (wechatWxAuthInFlight) return;
        // 只有曾经进入过授权覆盖，再回到前台才解锁（避免真机一直 visible 误解锁）
        if (!grantCoveredByNative && !wechatGrantOpened && !wechatGrantNavigating) {
            return;
        }
        if (!isLogin()) {
            resetWechatGrantLock();
            return;
        }
        resetWechatGrantLock();
    };

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            if (wechatGrantOpened || wechatGrantNavigating) {
                grantCoveredByNative = true;
            }
            return;
        }
        if (document.visibilityState === 'visible') {
            onPossiblyReturnedFromGrant();
        }
    });

    // 部分真机不触发 visibility，但会 pageshow
    window.addEventListener('pageshow', () => {
        onPossiblyReturnedFromGrant();
    });
}

/**
 * @param {{ force?: boolean }} [options]
 * force 仅跳过时间防抖，不能打断「已打开/正在打开」锁
 */
export function gotoWechatLogin(options = {}) {
    bindGrantVisibilityUnlock();
    if (isLogin()) {
        resetWechatGrantLock();
        return;
    }
    if (wechatWxAuthInFlight) {
        return;
    }
    // 已打开或正在打开：绝不重复 navigateTo（真机叠页主因）
    if (wechatGrantOpened || wechatGrantNavigating) {
        return;
    }
    const force = options && options.force === true;
    const now = Date.now();
    if (!force && now - lastGotoWechatLoginAt < GOTO_WECHAT_LOGIN_DEBOUNCE_MS) {
        return;
    }
    lastGotoWechatLoginAt = now;
    wechatGrantNavigating = true;
    try {
        wx.miniProgram.navigateTo({
            url: '/pages/grant/authorization',
            success: () => {
                wechatGrantOpened = true;
                wechatGrantNavigating = false;
            },
            fail: () => {
                resetWechatGrantLock();
            }
        });
    } catch (e) {
        resetWechatGrantLock();
        console.log('err: ', e)
    }
}

function isH5LoginPath(url = '') {
    const s = String(url || '');
    return s === '/login' || s.indexOf('/login') !== -1;
}

function clearWechatNowLoginFlag() {
    try {
        VueCookieNext.setCookie(
            'Wechat-Cache-authKey-now-login', "0", { expire: 0 }
        );
    } catch (e) {
        console.log('err: ', e)
    }
}

function isH5LoginRoute(to) {
    const name = getObjectValue(to, 'name', '');
    const fullPath = getObjectValue(to, 'fullPath', '') || getObjectValue(to, 'path', '');
    return name === 'login' || name === 'testDon' || isH5LoginPath(fullPath);
}

/**
 * 授权成功后的去向：当前是分享/业务落地页则留在该页；登录页才回上一页或首页。
 * @returns {string|undefined} undefined 表示 next() 留在当前 to
 */
function resolveAfterWechatAuth(to) {
    if (to && !isH5LoginRoute(to)) {
        clearWechatNowLoginFlag();
        return undefined;
    }
    const jumpUrl = getUserLastRequestUrl();
    clearWechatNowLoginFlag();
    if (jumpUrl && jumpUrl !== '/' && !isH5LoginPath(jumpUrl)) {
        return jumpUrl;
    }
    return '/';
}

function finishAfterWechatAuth(to, finish) {
    if (typeof finish != 'function') return;
    finish(resolveAfterWechatAuth(to));
}

/**
 * 微信登录成功以后跳转到上一个页面
 * 
 * @returns boolean
 */
export function wechatLoginJumpPage(next = null) {
    try {
        const isNowLoginWechat = VueCookieNext.getCookie('Wechat-Cache-authKey-now-login');
        if (typeof isNowLoginWechat == 'string' && isNowLoginWechat == '1') {
            if (typeof next == 'function') {
                const jumpUrl = getUserLastRequestUrl();
                clearWechatNowLoginFlag();
                if (!jumpUrl || jumpUrl == '/' || isH5LoginPath(jumpUrl)) {
                    return false;
                }
                next(jumpUrl);
            }
            return true;
        }
    } catch (e) {
        console.log('err: ', e)
    }
    return false;
}

/**
 * 微信小程序环境下，url访问前缀判断
 *
 * @param object to
 * @returns
 */
export function isJumpToWechatLogin(to, next = null) {
    if (typeof next != 'function') next = () => { };

    const routerName = getObjectValue(to, 'name', '');
    if (typeof routerName != 'string' || '' == (routerName + '').trim()) {
        return;
    }

    let nextCalled = false;
    const finish = (loc) => {
        if (nextCalled) return;
        nextCalled = true;
        if (typeof loc === 'undefined') next();
        else next(loc);
    };

    const wechatAuthCode = getObjectValue(to, 'query', 'wechatAuthCode', '');
    const wechatPhoneLoginCode = getObjectValue(to, 'query', 'wechatPhoneLoginCode', '');

    /**
     * 小程序返回再进 index 会重新 wx.login，URL 又带 wechatAuthCode。
     * 若本地已登录（cookie 或 localStorage 恢复），直接丢掉这些参数，禁止再打 wxAuth，避免冲掉登录态。
     */
    const stripWechatAuthQueryAndContinue = () => {
        const rawQuery = getObjectValue(to, 'query', {}) || {};
        const query = { ...rawQuery };
        delete query.wechatAuthCode;
        delete query.wechatPhoneLoginCode;
        wechatGrantOpened = false;
        wechatWxAuthInFlight = false;
        finish({
            name: routerName,
            params: to.params,
            query,
            hash: to.hash || '',
            replace: true,
        });
    };

    if (
        (typeof wechatAuthCode == 'string' && '' != (wechatAuthCode + '').trim()) ||
        (typeof wechatPhoneLoginCode == 'string' && '' != (wechatPhoneLoginCode + '').trim())
    ) {
        if (isLogin()) {
            stripWechatAuthQueryAndContinue();
            return true;
        }
    }

    // 必须先消化授权回跳参数，否则会再次 gotoWechatLogin，表现为登录两次
    if (typeof wechatAuthCode == 'string' && '' != (wechatAuthCode + '').trim()) {
        wechatWxAuthInFlight = true;
        getApiDatas('login', 'wxAuth', {
            code: wechatAuthCode
        }).then((apiRes) => {
            const authKey = getObjectValue(apiRes, 'data', 'authKey', '');
            if (typeof authKey == 'string' && (authKey + '').trim().length >= 5) {
                VueCookieNext.setCookie(
                    'Wechat-Cache-authKey', authKey, { expire: 0 }
                );
            }

            const loginUid = getObjectValue(apiRes, 'data', 'uid', '');
            const loginToken = getObjectValue(apiRes, 'data', 'token', '');
            if (
                typeof loginUid == 'string' && loginUid.trim().length >= 5 &&
                typeof loginToken == 'string' && loginToken.trim().length >= 5
            ) {
                saveUserInfo(loginUid, loginToken, {
                    success: () => {
                        wechatGrantOpened = false;
                        finishAfterWechatAuth(to, finish);
                    },
                    fail: () => {
                        alert('登录失败1');
                        finishAfterWechatAuth(to, finish);
                    }
                });
                return;
            }

            if (typeof wechatPhoneLoginCode == 'string' && wechatPhoneLoginCode.trim() !== '') {
                wechatPhoneLogin(to, finish);
                return;
            }

            const code = getObjectValue(apiRes, 'code', -1);
            if (0 != code) {
                finishAfterWechatAuth(to, finish);
                return;
            }
            // 只有 authKey、尚未拿到 uid：留在当前落地页等手机号回跳，登录页才回首页
            finishAfterWechatAuth(to, finish);
        }).catch(() => {
            finishAfterWechatAuth(to, finish);
        }).finally(() => {
            wechatWxAuthInFlight = false;
            if (isLogin()) {
                wechatGrantOpened = false;
            }
        });
        return true;
    }
    if (typeof wechatPhoneLoginCode == 'string' && '' != (wechatPhoneLoginCode + '').trim()) {
        wechatWxAuthInFlight = true;
        wechatPhoneLogin(to, finish);
        return true;
    }

    if (routerName == 'login' || routerName == 'testDon') {
        if (isLogin()) {
            finishAfterWechatAuth(to, finish);
            return true;
        }
        try {
            VueCookieNext.setCookie(
                'Wechat-Cache-authKey-now-login', "1", { expire: 0 }
            );
        } catch (e) {
            console.log('err: ', e)
        }
        gotoWechatLogin();
        finishAfterWechatAuth(to, finish);
        return true;
    }
    if (isLogin()) {
        if (isH5LoginRoute(to) && wechatLoginJumpPage(finish)) {
            return true;
        }
        return false;
    }
    // 未登录访问需登录页：只拉小程序授权，留在当前页，不要切到 H5 /login
    if (to.meta && to.meta.requireAuth) {
        try {
            VueCookieNext.setCookie(
                'Wechat-Cache-authKey-now-login', "1", { expire: 0 }
            );
        } catch (e) {
            console.log('err: ', e)
        }
        gotoWechatLogin();
        finish();
        return true;
    }
    return false;
}

/**
 * 微信小程序环境下，url访问前缀判断
 * 
 * @param object to 
 * @returns 
 */
export function wechatPhoneLogin(to, next = null) {
    if (typeof next != 'function') next = () => { };

    const wechatPhoneLoginCode = getObjectValue(to, 'query', 'wechatPhoneLoginCode', '');
    if (!(
        typeof wechatPhoneLoginCode == 'string' && '' != (wechatPhoneLoginCode + '').trim()
    )) {
        next();
        return false;
    }
    const authKey = VueCookieNext.getCookie(
        'Wechat-Cache-authKey'
    );
    if (!(typeof authKey == 'string' && (authKey + '').trim().length >= 5)) {
        wechatWxAuthInFlight = false;
        next();
        return false;
    }
    getApiDatas('login', 'getPhoneNumber', {
        authKey: authKey,
        code: wechatPhoneLoginCode
    }).then((apiRes) => {
        const loginUid = getObjectValue(apiRes, 'data', 'uid', '');
        const loginToken = getObjectValue(apiRes, 'data', 'token', '');
        if (
            typeof loginUid == 'string' && loginUid.trim().length >= 5 &&
            typeof loginToken == 'string' && loginToken.trim().length >= 5
        ) {
            saveUserInfo(loginUid, loginToken, {
                success: () => {
                    wechatGrantOpened = false;
                    finishAfterWechatAuth(to, next);
                },
                fail: () => {
                    finishAfterWechatAuth(to, next);
                }
            });
            return;
        }
        finishAfterWechatAuth(to, next);
    }).finally(() => {
        wechatWxAuthInFlight = false;
        if (isLogin()) {
            wechatGrantOpened = false;
        }
    });
    return true;
}

/**
 * 跳转到微信小程序页面
 */
export function gotoWechatPage(pageUrl = '') {
    try {
        wx.miniProgram.navigateTo({
            url: pageUrl
        });
    } catch (e) {
        console.log('err: ', e)
    }
}
